import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { fetchAgencies, updateAgency } from "../../api/apiClient";
import { defaultAgencyId } from "./agencySelection";
import { useTenant } from "../../contexts/TenantContext";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";

export default function AgencyDetails() {
  const { user } = useTenant();
  const { tenantId: activeTenantId } = useActiveTenant();
  const navigate = useNavigate();

  const [agency, setAgency] = useState(null);
  const [form, setForm] = useState({
    countiesServed: "",
    taskForces: "",
    notificationsEnabled: true,
  });
  const isAuthorized = user?.roles?.some((r) =>
    ["AGENCY_ADMIN", "ADMIN", "GLOBAL_ADMIN"].includes(r),
  );

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!activeTenantId || !isAuthorized) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchAgencies(activeTenantId)
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        const a = arr.find((x) => x.id === defaultAgencyId(arr, user));
        if (a) {
          setAgency(a);
          setForm({
            countiesServed: a.countiesServed || "",
            taskForces: a.taskForces || "",
            notificationsEnabled: a.notificationsEnabled ?? true,
          });
        }
      })
      .catch(() => setLoadError("Failed to load agency data."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId, isAuthorized]);

  function handleChange(field, value) {
    setSaved(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await updateAgency(activeTenantId, agency.id, form);
      setAgency(updated);
      setSaved(true);
    } catch {
      setSaveError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!user || !isAuthorized) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to view this page.
        </Alert>
      </Container>
    );
  }

  if (!activeTenantId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <NoTenantSelected message="Select a training center from the top bar to manage agency details." />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/agency")}
        sx={{ mb: 3, textTransform: "none", color: "text.secondary" }}
      >
        Back to Agency Management
      </Button>

      <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5, fontFamily: "var(--font-display)" }}>
        Agency Details
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Update operational details and notification preferences for your agency.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error">{loadError}</Alert>
      ) : !agency ? (
        <Alert severity="warning">No agency found for your account.</Alert>
      ) : (
        <Stack spacing={3}>
          {saved && (
            <Alert severity="success" onClose={() => setSaved(false)}>
              Agency details saved successfully.
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}

          {/* Legal name — read-only */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                display: "block",
                mb: 0.5,
              }}
            >
              Agency Legal Name
            </Typography>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
                bgcolor: "grey.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {agency.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  bgcolor: "grey.200",
                  color: "text.secondary",
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  fontSize: "0.6rem",
                }}
              >
                Read-only
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Legal name changes require administrator approval.
            </Typography>
          </Box>

          <TextField
            label="Counties Served"
            fullWidth
            size="small"
            value={form.countiesServed}
            onChange={(e) => handleChange("countiesServed", e.target.value)}
            helperText="Separate multiple counties with a comma"
          />

          <TextField
            label="Active Memberships / Task Forces"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={form.taskForces}
            onChange={(e) => handleChange("taskForces", e.target.value)}
            helperText="List task forces, mutual aid agreements, or regional memberships"
          />

          <Divider />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
              bgcolor: "grey.50",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Notification Preferences
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Receive email alerts when an associated member completes a course
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.notificationsEnabled}
                  onChange={(e) =>
                    handleChange("notificationsEnabled", e.target.checked)
                  }
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {form.notificationsEnabled ? "Enabled" : "Disabled"}
                </Typography>
              }
              labelPlacement="start"
              sx={{ ml: 0, gap: 1 }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </Box>
        </Stack>
      )}
    </Container>
  );
}
